import { useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  CalendarBlank,
  ArrowRight,
  CaretLeft,
  CaretRight,
  Trophy,
  Tent,
  SoccerBall,
  UsersThree,
  ChartBar,
  Globe,
  UserPlus,
  HourglassMedium,
  PaperPlaneTilt,
  Megaphone,
  type Icon,
} from '@phosphor-icons/react';

import { listActivitiesInRange, type ActivityType } from '@/api/activities';
import { listPulseSchedules } from '@/api/pulse';
import { listWebsites } from '@/api/websites';
import { listSignupSummary } from '@/api/signups';
import { listBroadcasts } from '@/api/broadcasts';
import { listLocations } from '@/api/locations';
import { useActivityFixtures } from '@/hooks/useActivityFixtures';
import { useOrgCrest } from '@/hooks/useOrgCrest';
import { matchDisplay, scoreLabel, type MatchDisplay } from '@/lib/matchDisplay';
import { MatchSides } from '@/components/schedule/MatchSides';
import { Skeleton } from '@/components/ui/skeleton';

// WeekAhead — the admin's week at a glance. This is NOT the training calendar:
// routine sessions are deliberately excluded. It surfaces the system + club
// events an admin actually plans around: notable happenings (tournaments,
// camps, matches, meetings), a member pulse survey that's about to auto-send,
// a website scheduled to publish, and a broadcast (utskick) scheduled to send.
// Each is future-dated within 7 days, grouped by day, and links to where the
// admin manages it.
//
// Signup windows are surfaced too: a team whose public signup is scheduled to
// open (or is set to auto-close) within the window shows up so the admin sees
// intake changes coming without opening the Teams page.
//
// (Scheduled notices still aren't shown — notices send immediately. When
// notice scheduling lands, add it here the same way scheduled broadcasts are.)

type EventKind =
  | 'cup'
  | 'camp'
  | 'match'
  | 'meeting'
  | 'orgWide'
  | 'pulse'
  | 'website'
  | 'broadcast'
  | 'signupOpens'
  | 'signupCloses';

interface WeekEvent {
  id: string;
  at: string; // RFC3339
  kind: EventKind;
  label: string;
  detail?: string;
  to: string;
  // Venue name, when the activity has a location. Rendered on match rows;
  // knowing *where* is half of what makes a fixture actionable.
  place?: string;
  match?: MatchDisplay;
}

const kindIcon: Record<EventKind, Icon> = {
  cup: Trophy,
  camp: Tent,
  match: SoccerBall,
  meeting: UsersThree,
  orgWide: Megaphone,
  pulse: ChartBar,
  website: Globe,
  broadcast: PaperPlaneTilt,
  signupOpens: UserPlus,
  signupCloses: HourglassMedium,
};

const kindAccent: Record<EventKind, string> = {
  cup: 'var(--orange)',
  camp: 'var(--aqua)',
  match: 'var(--lime)',
  meeting: 'var(--pink)',
  orgWide: 'var(--lime)',
  pulse: 'var(--lime)',
  website: 'var(--aqua)',
  broadcast: 'var(--pink)',
  signupOpens: 'var(--lime)',
  signupCloses: 'var(--orange)',
};

// Notable activity types only — training/other are routine and excluded.
// Exception: org-wide activities of ANY type are always surfaced (a club-wide
// training still matters to everyone) — see the activities loop below.
const NOTABLE: ReadonlySet<ActivityType> = new Set<ActivityType>(['cup', 'camp', 'match', 'meeting']);

// How far forward the week toggle can page. 8 weeks is plenty of lookahead for
// "what's coming up" without inviting aimless scrolling into the far future.
const MAX_WEEK_OFFSET = 8;

export function WeekAhead({ slug, token }: { slug: string; token: string | null }) {
  const { t, i18n } = useTranslation('common');

  // weekOffset pages the 7-day window forward: 0 = the next 7 days, 1 = the 7
  // after that, and so on. Never negative — the widget is forward-looking.
  const [weekOffset, setWeekOffset] = useState(0);

  const windowStart = new Date();
  windowStart.setDate(windowStart.getDate() + weekOffset * 7);
  const windowEnd = new Date(windowStart);
  windowEnd.setDate(windowStart.getDate() + 7);
  const startMs = windowStart.getTime();
  const endMs = windowEnd.getTime();

  const results = useQueries({
    queries: [
      {
        // Range is server-filtered, so it's keyed by the offset; the others
        // return all rows and are filtered to the window client-side.
        queryKey: ['home-week-activities', slug, weekOffset],
        queryFn: () => listActivitiesInRange(token, slug, windowStart, windowEnd),
        enabled: !!token && !!slug,
      },
      {
        queryKey: ['home-week-pulse', slug],
        queryFn: () => listPulseSchedules(token, slug),
        enabled: !!token && !!slug,
      },
      {
        queryKey: ['home-week-websites', slug],
        queryFn: () => listWebsites(token, slug),
        enabled: !!token && !!slug,
      },
      {
        queryKey: ['home-week-signups', slug],
        queryFn: () => listSignupSummary(token, slug),
        enabled: !!token && !!slug,
      },
      {
        queryKey: ['home-week-broadcasts', slug],
        queryFn: () => listBroadcasts(token, slug),
        enabled: !!token && !!slug,
      },
      {
        // Venue names for the match rows. Same key the activity sheet uses, so
        // the two share one cache entry.
        queryKey: ['locations', slug, ''],
        queryFn: () => listLocations(token, slug),
        enabled: !!token && !!slug,
      },
    ],
  });
  const [activitiesQ, pulseQ, websitesQ, signupsQ, broadcastsQ, locationsQ] = results;
  const isLoading = results.some((r) => r.isLoading);

  // Opponent / crest / home-away / score for the matches in this window, plus
  // our own crest so both sides of a fixture carry a logo.
  const fixtureByActivity = useActivityFixtures(token, slug, activitiesQ.data ?? []);
  const orgCrest = useOrgCrest(token, slug);

  const events = useMemo<WeekEvent[]>(() => {
    const start = startMs;
    const end = endMs;
    const inWindow = (iso?: string | null) => {
      if (!iso) return false;
      const ms = Date.parse(iso);
      return !Number.isNaN(ms) && ms >= start && ms <= end;
    };

    const list: WeekEvent[] = [];

    const placeByLocation = new Map<string, string>();
    for (const loc of locationsQ.data?.locations ?? []) placeByLocation.set(loc.id, loc.name);

    for (const a of activitiesQ.data ?? []) {
      const notable = NOTABLE.has(a.activity_type);
      // Show notable types, plus any org-wide activity regardless of type.
      if (!notable && !a.org_wide) continue;
      const detail = a.teams.map((tm) => tm.name).join(', ');
      // A notable org-wide activity (an org-wide match) keeps its type icon;
      // an org-wide routine activity (training/other) gets the club-wide icon
      // so it reads as "everyone" rather than a mislabeled game.
      const kind: EventKind = notable ? (a.activity_type as EventKind) : 'orgWide';
      const place = a.location_id ? placeByLocation.get(a.location_id) : undefined;
      // Our side of the fixture: the first linked team (activities carry one
      // team in practice), falling back to the activity title so a game whose
      // team chip is missing still reads as "us vs them".
      const match = matchDisplay(fixtureByActivity.get(a.id), {
        name: a.teams[0]?.name || a.title,
        logoUrl: orgCrest,
      });
      list.push({
        id: `activity-${a.id}`,
        at: a.starts_at,
        kind,
        label: a.title,
        to: `/orgs/${slug}/schedule/activities/${a.id}`,
        ...(detail ? { detail } : {}),
        ...(place ? { place } : {}),
        ...(match ? { match } : {}),
      });
    }

    for (const s of pulseQ.data?.schedules ?? []) {
      if (!s.enabled || !inWindow(s.nextRunAt)) continue;
      list.push({
        id: `pulse-${s.id}`,
        at: s.nextRunAt,
        kind: 'pulse',
        label: t('home.weekAhead.pulseSend'),
        // Same key namespace RoleBadge on the Pulse page uses — the audience
        // labels live under pulse.create.role.* in all five locales. No
        // defaultValue: PulseRole is a closed union whose members are all
        // translated, so a miss should surface loudly rather than leak the
        // raw wire value ('guardian') into the UI.
        detail: t(`pulse.create.role.${s.targetRole}`),
        to: `/orgs/${slug}/pulse`,
      });
    }

    for (const w of websitesQ.data ?? []) {
      if (!inWindow(w.scheduled_publish_at)) continue;
      list.push({
        id: `website-${w.id}`,
        at: w.scheduled_publish_at!,
        kind: 'website',
        label: t('home.weekAhead.websitePublish'),
        detail: w.name,
        to: `/orgs/${slug}/website`,
      });
    }

    // Scheduled broadcasts — a draft (status stays 'draft' until the worker
    // sends) carrying a future scheduled_at inside the window. Links to the
    // editor so the admin can review, reschedule, or cancel before it fires.
    for (const b of broadcastsQ.data ?? []) {
      if (b.status !== 'draft' || !inWindow(b.scheduled_at)) continue;
      list.push({
        id: `broadcast-${b.id}`,
        at: b.scheduled_at!,
        kind: 'broadcast',
        label: t('home.weekAhead.broadcastSend'),
        detail: b.subject,
        to: `/orgs/${slug}/broadcasts/${b.id}`,
      });
    }

    // Signup windows — only for teams whose signup is enabled. A future
    // opens_at is an upcoming intake; a future closes_at is a last-call the
    // admin plans around. Past bounds are irrelevant here.
    for (const s of signupsQ.data?.teams ?? []) {
      if (!s.enabled) continue;
      if (inWindow(s.opens_at)) {
        list.push({
          id: `signup-open-${s.team_id}`,
          at: s.opens_at!,
          kind: 'signupOpens',
          label: t('home.weekAhead.signupOpens'),
          detail: s.team_name,
          to: `/orgs/${slug}/teams`,
        });
      }
      if (inWindow(s.closes_at)) {
        list.push({
          id: `signup-close-${s.team_id}`,
          at: s.closes_at!,
          kind: 'signupCloses',
          label: t('home.weekAhead.signupCloses'),
          detail: s.team_name,
          to: `/orgs/${slug}/teams`,
        });
      }
    }

    return list.sort((x, y) => Date.parse(x.at) - Date.parse(y.at));
  }, [
    activitiesQ.data,
    pulseQ.data,
    websitesQ.data,
    signupsQ.data,
    broadcastsQ.data,
    locationsQ.data,
    fixtureByActivity,
    orgCrest,
    slug,
    t,
    startMs,
    endMs,
  ]);

  const groups = useMemo(() => groupByDay(events), [events]);

  const dayLabel = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language || undefined, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    }).format(new Date(iso));

  const timeLabel = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language || undefined, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(new Date(iso));

  // Range label for the current window, e.g. "22 jul – 28 jul" (inclusive last
  // day). Offset 0 gets a friendly "this week" instead.
  const shortDay = (d: Date) =>
    new Intl.DateTimeFormat(i18n.language || undefined, { day: 'numeric', month: 'short' }).format(d);
  const inclusiveEnd = new Date(windowEnd);
  inclusiveEnd.setDate(windowEnd.getDate() - 1);
  const rangeLabel =
    weekOffset === 0
      ? t('home.weekAhead.thisWeek')
      : `${shortDay(windowStart)} – ${shortDay(inclusiveEnd)}`;

  // Height is content-driven with a 500px floor — this used to be h-full, which
  // stretched the card to match the whole left column (tiles + quick actions +
  // the to-do list) and left a wall of empty space below "nothing planned". The
  // inner list keeps its own max-height + scroll.
  return (
    <section className="flex min-h-[500px] flex-col gap-3 rounded-2xl border border-border bg-card p-4 shadow-sm">
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2">
          <h2 className="flex items-center gap-2 font-heading text-sm font-bold uppercase tracking-wider text-muted-foreground">
            <CalendarBlank size={16} weight="bold" aria-hidden />
            {t('home.weekAhead.title')}
          </h2>
          <Link
            to={`/orgs/${slug}/schedule`}
            className="inline-flex items-center gap-1 rounded-pill px-2 py-1 text-xs font-semibold text-muted-foreground transition hover:text-foreground"
          >
            {t('home.weekAhead.viewAll')}
            <ArrowRight size={14} weight="bold" aria-hidden />
          </Link>
        </div>
        {/* Week toggle — page the 7-day window forward to see what's coming. */}
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            aria-label={t('home.weekAhead.prevWeek')}
            disabled={weekOffset === 0}
            onClick={() => setWeekOffset((o) => Math.max(0, o - 1))}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-border text-muted-foreground transition hover:text-foreground disabled:opacity-30"
          >
            <CaretLeft size={15} weight="bold" aria-hidden />
          </button>
          <span className="text-xs font-semibold text-foreground">{rangeLabel}</span>
          <button
            type="button"
            aria-label={t('home.weekAhead.nextWeek')}
            disabled={weekOffset >= MAX_WEEK_OFFSET}
            onClick={() => setWeekOffset((o) => Math.min(MAX_WEEK_OFFSET, o + 1))}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-border text-muted-foreground transition hover:text-foreground disabled:opacity-30"
          >
            <CaretRight size={15} weight="bold" aria-hidden />
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : groups.length === 0 ? (
        <p className="rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground">
          {t('home.weekAhead.empty')}
        </p>
      ) : (
        <div className="flex max-h-[28rem] flex-col gap-4 overflow-y-auto pr-1">
          {groups.map((g) => (
            <div key={g.key} className="flex flex-col gap-1.5">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground/60">
                {dayLabel(g.dayIso)}
              </h3>
              <ul className="flex flex-col gap-1.5">
                {g.items.map((e) => {
                  const IconCmp = kindIcon[e.kind];
                  // A fixture gets the game treatment: both sides with their own
                  // crest, home/away + venue underneath, score at the end once
                  // recorded. It drops the tinted kind icon — the two crests
                  // already say "this is a game, between these clubs", and a
                  // generic ball next to them is noise. Every other row keeps
                  // its icon: a pulse send or a scheduled broadcast has no other
                  // visual telling you what kind of thing it is. A match with no
                  // opponent set yet falls back to the generic row, icon and all.
                  const m = e.match;
                  const subParts = m
                    ? [
                        t(`schedule.match.homeAway.${m.isHome ? 'home' : 'away'}`),
                        e.place,
                        m.competition,
                      ].filter(Boolean)
                    : [e.detail, e.place].filter(Boolean);
                  const score = m ? scoreLabel(m) : null;
                  return (
                    <li key={e.id}>
                      <Link
                        to={e.to}
                        className="flex items-center gap-3 rounded-xl border border-border/70 bg-background p-2.5 transition hover:-translate-y-0.5 hover:shadow-sm motion-reduce:hover:translate-y-0"
                      >
                        <span className="w-11 shrink-0 text-sm font-semibold tabular-nums">
                          {timeLabel(e.at)}
                        </span>
                        {!m && (
                          <span
                            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
                            style={{ backgroundColor: `color-mix(in srgb, ${kindAccent[e.kind]} 36%, transparent)` }}
                            aria-hidden
                          >
                            <IconCmp size={16} weight="bold" />
                          </span>
                        )}
                        <span className="flex min-w-0 flex-1 flex-col">
                          {m ? (
                            // Stacked: this card sits in a narrow dashboard
                            // column where both sides on one line truncate to
                            // nothing legible.
                            <MatchSides
                              match={m}
                              size={18}
                              orientation="stacked"
                              className="text-sm font-medium"
                            />
                          ) : (
                            <span className="truncate text-sm font-medium">{e.label}</span>
                          )}
                          {subParts.length > 0 && (
                            <span className="truncate text-xs text-muted-foreground">
                              {subParts.join(' · ')}
                            </span>
                          )}
                        </span>
                        {score && (
                          <span className="shrink-0 rounded-pill bg-muted px-2 py-0.5 font-heading text-sm font-bold tabular-nums">
                            {score}
                          </span>
                        )}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

interface DayGroup {
  key: string; // local calendar-day key (dedupe)
  dayIso: string; // RFC3339 of the first event that day (drives the label)
  items: WeekEvent[];
}

function groupByDay(events: WeekEvent[]): DayGroup[] {
  const byKey = new Map<string, DayGroup>();
  const order: string[] = [];
  for (const e of events) {
    const d = new Date(e.at);
    const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    let group = byKey.get(key);
    if (!group) {
      group = { key, dayIso: e.at, items: [] };
      byKey.set(key, group);
      order.push(key);
    }
    group.items.push(e);
  }
  return order.map((k) => byKey.get(k)!);
}
